/*
 * Resolve a game's icon URL from a name→iconUrl map (built by useAllGameIcons,
 * keyed by lowercase raw catalog name), tolerant of the naming drift between
 * platform stream categories and our game catalog. Centralized so the sub-rail,
 * filter rail, and cards all resolve identically.
 *
 * Cascade: exact → base-before-":" → alias → normalized-exact → fuzzy contains.
 */

/**
 * Strip everything but alphanumerics + lowercase, so "Counter-Strike 2" and
 *  "counterstrike2" collapse to the same key.
 */
function normalize(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/*
 * Common stream-category → catalog aliases (both sides normalized). Platforms
 * label categories differently than our catalog (abbreviations, franchise vs
 * title), so a fuzzy contains alone would mis-hit; explicit aliases pin the
 * frequent ones.
 */
const GAME_NAME_ALIASES: Record<string, string> = {
    cs: "counterstrike2",
    cs2: "counterstrike2",
    csgo: "counterstrikeglobaloffensive",
    cod: "callofduty",
    codwarzone: "callofdutywarzone",
    warzone: "callofdutywarzone",
    lol: "leagueoflegends",
    pubg: "playerunknownsbattlegrounds",
    wow: "worldofwarcraft",
    dota: "dota2",
    apex: "apexlegends",
    valorant: "valorant",
    ow2: "overwatch2",
    overwatch: "overwatch2",
    rl: "rocketleague",
    r6: "tomclancysrainbowsixsiege",
    rainbowsix: "tomclancysrainbowsixsiege",
};

export function resolveGameIcon(
    iconMap: Map<string, string> | undefined,
    gameName: string | undefined,
): string | undefined {
    if (!iconMap || !gameName) return undefined;

    const lower = gameName.trim().toLowerCase();

    // 1. exact (map keys are lowercase raw names)
    const exact = iconMap.get(lower);
    if (exact) return exact;

    // 2. base before a ":" — "Call of Duty: Warzone" → "call of duty"
    const base = lower.split(":")[0].trim();
    if (base !== lower) {
        const baseHit = iconMap.get(base);
        if (baseHit) return baseHit;
    }

    // 3–5. normalize + alias + fuzzy contains against the catalog.
    const norm = normalize(gameName);
    if (!norm) return undefined;
    const target = GAME_NAME_ALIASES[norm] ?? norm;

    // normalized-exact / alias
    for (const [key, url] of iconMap) {
        if (normalize(key) === target) return url;
    }
    // fuzzy contains (guard length so short tokens don't over-match)
    for (const [key, url] of iconMap) {
        const nk = normalize(key);
        if (nk.length >= 4 && (nk.includes(target) || target.includes(nk))) return url;
    }
    return undefined;
}
